import layout from '../layout/layout';
import { useEffect, useMemo, useState, type DragEvent } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../Store/hooks';
import { getStudents } from '../Store/StudentSlices/getStudentsSlice';
import {
  getStudentDetail,
  type StudentDetailResponse,
} from '../Store/StudentSlices/getStudentDetailSlice';
import { updateBookingStatus } from '../Store/StudentSlices/updateBookingStatusSlice';
import { createNote } from '../Store/StudentSlices/createNoteSlice';
import { updateStudentContact } from '../Store/StudentSlices/updateStudentContactSlice';
import StudentsPanel from '../Component/Home/StudentsPanel';
import StudentDetailPanel from '../Component/Home/StudentDetailPanel';
import ResourcePreviewModal, {
  type PreviewKind,
} from '../Component/Home/ResourcePreviewModal';
import type {
  ApiStudent,
  DetailTab,
  ResourceType,
  SharedResource,
  SortDirection,
  SortKey,
  Student,
  StudentStatus,
  StudentNote,
} from '../Component/Home/types';

const statusFallback: StudentStatus = 'Ongoing';
const normalizeStatus = (value: string): StudentStatus => {
  const normalized = value.trim().toLowerCase();
  if (['ongoing', 'active', 'in progress'].includes(normalized)) {
    return 'Ongoing';
  }
  if (['not started', 'not_started', 'new'].includes(normalized)) {
    return 'Not Started';
  }
  if (['on break', 'on_break', 'paused'].includes(normalized)) {
    return 'On Break';
  }
  if (['completed', 'done', 'finished'].includes(normalized)) {
    return 'Completed';
  }
  return statusFallback;
};

const formatSessionDate = (value: string) => {
  if (!value || value.startsWith('0001-01-01')) {
    return 'No sessions yet';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'No sessions yet';
  }
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('');

const buildAbout = (name: string, focusArea: string, goal: string) => {
  const focusCopy = focusArea ? ` in ${focusArea}` : '';
  const goalCopy = goal ? ` with a focus on ${goal.toLowerCase()}` : '';
  return `I'm ${name}, working toward my next coaching milestone${focusCopy}${goalCopy}.`;
};

const mapFileTypeToResourceType = (value?: string): ResourceType => {
  const normalized = value?.toLowerCase() ?? '';
  if (normalized === 'pdf') {
    return 'PDF';
  }
  if (['image', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(normalized)) {
    return 'Image';
  }
  if (normalized === 'link') {
    return 'Link';
  }
  // Default to PDF for unrecognized types
  return 'PDF';
};

const formatResourceDate = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatNoteDate = (note: any) => {
  const date = note?.date;
  if (date?.month && date?.day && date?.year) {
    return `${date.month} ${date.day}, ${date.year}`;
  }
  if (note?.created_at) {
    return formatResourceDate(note.created_at);
  }
  return '';
};

const mapStudentResources = (resources: unknown): SharedResource[] => {
  if (!Array.isArray(resources)) {
    return [];
  }
  return (resources
    .map((resource, index) => {
      if (typeof resource === 'string') {
        return {
          id: `resource-${index}`,
          name: resource,
          type: 'File' as ResourceType,
          addedOn: '',
        };
      }
      if (resource && typeof resource === 'object') {
        const value = resource as any;
        return {
          id: value.id ?? `resource-${index}`,
          name: value.title ?? value.file_name ?? 'Resource',
          type: mapFileTypeToResourceType(value.file_type) as ResourceType,
          addedOn: formatResourceDate(value.created_at),
          link: value.file_url,
        };
      }
      return {
        id: `resource-${index}`,
        name: 'Resource',
        type: 'File' as ResourceType,
        addedOn: '',
      };
    })
    .filter((resource) => resource.name.trim().length > 0)) as SharedResource[];
};

const mapStudentNotes = (notes: unknown): StudentNote[] => {
  if (!Array.isArray(notes)) {
    return [];
  }
  return notes
    .map((note, index) => {
      if (typeof note === 'string') {
        return {
          id: `note-${index}`,
          content: note,
          dateLabel: '',
          createdAt: '',
        };
      }
      if (note && typeof note === 'object') {
        const value = note as any;
        return {
          id: value.id ?? `note-${index}`,
          content: value.content ?? '',
          dateLabel: formatNoteDate(value),
          createdAt: value.created_at ?? '',
        };
      }
      return {
        id: `note-${index}`,
        content: '',
        dateLabel: '',
        createdAt: '',
      };
    })
    .filter((note) => note.content.trim().length > 0);
};

const mapStudentDetail = (student: StudentDetailResponse): Student => {
  const name = student.name || 'Student';
  const focusArea = student.focus_area || 'General Coaching';
  const goals = Array.isArray(student.goals) ? student.goals : [];
  const primaryGoal = goals[0] || 'Coaching Plan';
  return {
    id: student.user_id,
    name,
    initials: getInitials(name),
    status: normalizeStatus(student.status || ''),
    focus: focusArea,
    goal: primaryGoal,
    sessions: student.sessions_attended ?? 0,
    lastSession: 'No sessions yet',
    sessionsAttended: student.sessions_attended ?? 0,
    sessionsMissed: student.sessions_missed ?? 0,
    about: student.about || buildAbout(name, focusArea, primaryGoal),
    focusArea,
    goals,
    email: student.email || '',
    phone: student.phone || '',
    linkedIn: false,
    lastSessionNotes: 'No session notes yet.',
    notes: mapStudentNotes(student.notes),
    resources: mapStudentResources(student.resources),
  };
};

const mapStudentFromApi = (student: ApiStudent): Student => {
  const name = student.name || 'Student';
  const focusArea = student.focus_area || 'General Coaching';
  const goal = student.goal || 'Coaching Plan';
  const totalSessions = student.total_sessions ?? 0;
  return {
    id: student.user_id,
    name,
    initials: getInitials(name),
    status: normalizeStatus(student.status || ''),
    focus: focusArea,
    goal,
    sessions: totalSessions,
    lastSession: formatSessionDate(student.last_session_date),
    sessionsAttended: totalSessions,
    sessionsMissed: 0,
    about: buildAbout(name, focusArea, goal),
    focusArea,
    goals: goal ? [goal] : [],
    email: '',
    phone: '',
    linkedIn: false,
    lastSessionNotes: 'No session notes yet.',
    notes: [],
    resources: [],
  };
};

const parseLastSessionValue = (value: string) => {
  if (!value || value === 'No sessions yet') {
    return 0;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const getSortValue = (student: Student, key: SortKey) => {
  switch (key) {
    case 'name':
      return student.name;
    case 'status':
      return student.status;
    case 'focus':
      return student.focus;
    case 'goal':
      return student.goal;
    case 'sessions':
      return student.sessions;
    case 'lastSession':
      return parseLastSessionValue(student.lastSession);
    default:
      return student.name;
  }
};

const statusStyles: Record<StudentStatus, string> = {
  Ongoing: 'bg-[#ECFDF3] text-emerald-700',
  'Not Started': 'bg-[#FEF3F2] text-rose-700',
  'On Break': 'bg-[#FFFAEB] text-amber-700',
  Completed: 'bg-[#F2F4F7] text-slate-700',
};

const statusOptions: Array<{ value: StudentStatus; label: string; text: string }> =
  [
    { value: 'Ongoing', label: 'Ongoing', text: 'text-emerald-600' },
    { value: 'On Break', label: 'On Break', text: 'text-amber-500' },
    { value: 'Completed', label: 'Completed', text: 'text-slate-900' },
  ];

const resourceTypeStyles: Record<ResourceType, { badge: string; icon: string }> = {
  File: {
    badge: 'bg-rose-100 text-rose-600',
    icon: 'bg-rose-50 text-rose-500',
  },
  Link: {
    badge: 'bg-blue-100 text-blue-600',
    icon: 'bg-blue-50 text-blue-500',
  },
  Video: {
    badge: 'bg-violet-100 text-violet-600',
    icon: 'bg-violet-50 text-violet-500',
  },
  Image: {
    badge: 'bg-amber-100 text-amber-600',
    icon: 'bg-amber-50 text-amber-500',
  },
  PDF: {
    badge: 'bg-rose-100 text-rose-600',
    icon: 'bg-rose-50 text-rose-500',
  },
};

const listGridCols =
  'grid-cols-[minmax(240px,_2fr)_repeat(5,_minmax(140px,_1fr))_minmax(120px,_1fr)]';

const statusColumns = [
  {
    status: 'Ongoing',
    label: 'Ongoing',
    text: 'text-emerald-700',
    badge: 'bg-emerald-100',
    panel: 'bg-emerald-50',
    panelColor: '#ECFDF3',
  },
  {
    status: 'Not Started',
    label: 'Not Started',
    text: 'text-rose-700',
    badge: 'bg-rose-100',
    panel: 'bg-rose-50',
    panelColor: '#FEF3F2',
  },
  {
    status: 'On Break',
    label: 'On Break',
    text: 'text-amber-700',
    badge: 'bg-amber-100',
    panel: 'bg-amber-50',
    panelColor: '#FFFAEB',
  },
  {
    status: 'Completed',
    label: 'Completed',
    text: 'text-slate-700',
    badge: 'bg-slate-200',
    panel: 'bg-slate-100',
    panelColor: '#F2F4F7',
  },
] as const;

const detailTabs: DetailTab[] = [
  'About',
  'Last Session',
  'Notes',
  'Shared Resources',
];

const Home = () => {
  const dispatch = useAppDispatch();
  const { loading, students: studentsPayload, error } = useAppSelector(
    (state) => state.getStudent
  );
  const {
    loading: detailLoading,
    data: studentDetail,
    error: detailError,
  } = useAppSelector((state) => state.getStudentDetail);
  const {
    loading: statusUpdating,
    updatingId: updatingStatusId,
  } = useAppSelector((state) => state.updateBookingStatus);
  const { loading: notePosting } = useAppSelector((state) => state.createNote);
  const { loading: contactUpdating } = useAppSelector(
    (state) => state.updateStudentContact
  );
  const apiStudents = useMemo(() => {
    const data =
      (studentsPayload as any)?.response ??
      (studentsPayload as any)?.data ??
      studentsPayload ??
      [];
    return Array.isArray(data) ? (data as ApiStudent[]) : [];
  }, [studentsPayload]);
  const mappedApiStudents = useMemo(
    () => apiStudents.map(mapStudentFromApi),
    [apiStudents]
  );
  const [students, setStudents] = useState<Student[]>([]);
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const { studentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DetailTab>('About');
  const [openStatusId, setOpenStatusId] = useState<string | null>(null);
  const [detailStatusOverride, setDetailStatusOverride] = useState<
    Record<string, StudentStatus>
  >({});
  const [showNoteEditor, setShowNoteEditor] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailOverride, setEmailOverride] = useState('');
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [phoneOverride, setPhoneOverride] = useState('');
  const [previewResource, setPreviewResource] =
    useState<SharedResource | null>(null);
  const [previewKind, setPreviewKind] = useState<PreviewKind>('none');

  const selectedStudent = students.find((student) => student.id === studentId);
  const mappedDetail = studentDetail ? mapStudentDetail(studentDetail) : null;
  const activeStudent =
    mappedDetail && mappedDetail.id === studentId
      ? mappedDetail
      : selectedStudent;
  const resolvedActiveStudent = activeStudent
    ? {
        ...activeStudent,
        status:
          detailStatusOverride[activeStudent.id] ?? activeStudent.status,
      }
    : null;
  const displayEmail =
    emailOverride || resolvedActiveStudent?.email || '';
  const displayPhone =
    phoneOverride || resolvedActiveStudent?.phone || '';
  const currentEmail =
    emailOverride || resolvedActiveStudent?.email || '';
  const currentPhone =
    phoneOverride || resolvedActiveStudent?.phone || '';
  const isDetailView = Boolean(studentId);
  const isLoading = loading;
  const hasError = Boolean(error);
  const hasDetailError = Boolean(detailError);

  useEffect(() => {
    const requestedTab = (location.state as any)?.tab as DetailTab | undefined;
    if (requestedTab && detailTabs.includes(requestedTab)) {
      setActiveTab(requestedTab);
      return;
    }
    setActiveTab('About');
  }, [location.state, studentId]);

  useEffect(() => {
    if (studentId) {
      dispatch(getStudentDetail(studentId));
    }
  }, [dispatch, studentId]);

  useEffect(() => {
    setShowNoteEditor(false);
    setNoteDraft('');
    setIsEditingEmail(false);
    setEmailDraft('');
    setEmailOverride('');
    setIsEditingPhone(false);
    setPhoneDraft('');
    setPhoneOverride('');
  }, [studentId]);

  useEffect(() => {
    if (studentId) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [studentId]);

  useEffect(() => {
    if (!showNoteEditor) {
      setNoteDraft('');
    }
  }, [showNoteEditor]);

  useEffect(() => {
    if (!openStatusId) {
      return;
    }
    const handleClose = () => setOpenStatusId(null);
    window.addEventListener('click', handleClose);
    return () => window.removeEventListener('click', handleClose);
  }, [openStatusId]);

  useEffect(() => {
    dispatch(getStudents());
  }, [dispatch]);

  useEffect(() => {
    setStudents(mappedApiStudents);
  }, [mappedApiStudents]);

  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) {
      return students;
    }
    const query = searchQuery.toLowerCase();
    return students.filter(
      (student) =>
        student.name.toLowerCase().includes(query) ||
        student.focus.toLowerCase().includes(query) ||
        student.goal.toLowerCase().includes(query) ||
        student.email.toLowerCase().includes(query)
    );
  }, [students, searchQuery]);

  const sortedStudents = useMemo(() => {
    if (!filteredStudents.length) {
      return [];
    }
    return [...filteredStudents].sort((a, b) => {
      const aValue = getSortValue(a, sortKey);
      const bValue = getSortValue(b, sortKey);
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const comparison = String(aValue).localeCompare(String(bValue), undefined, {
        sensitivity: 'base',
      });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredStudents, sortKey, sortDirection]);

  const handleSort = (key: SortKey) => {
    setSortDirection((prevDirection) =>
      sortKey === key ? (prevDirection === 'asc' ? 'desc' : 'asc') : 'asc'
    );
    setSortKey(key);
  };

  const handleStatusSelect = async (
    student: Student,
    nextStatus: StudentStatus
  ) => {
    if (!student.id || statusUpdating) {
      return;
    }
    try {
      await dispatch(
        updateBookingStatus({
          studentId: student.id,
          status: nextStatus,
        })
      ).unwrap();
      setStudents((prev) =>
        prev.map((item) =>
          item.id === student.id ? { ...item, status: nextStatus } : item
        )
      );
      setOpenStatusId(null);
      setDetailStatusOverride((prev) => ({
        ...prev,
        [student.id]: nextStatus,
      }));
    } catch (error) {
      console.error('Failed to update status', error);
    }
  };

  const isNoteEmpty =
    noteDraft
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim().length === 0;
  const isPostDisabled = notePosting || isNoteEmpty;

  const noteModules = useMemo(
    () => ({
      toolbar: {
        container: '#note-toolbar',
      },
    }),
    []
  );

  const noteFormats = ['bold', 'italic', 'underline', 'list', 'align', 'link'];

  const handleEmailSave = async () => {
    const nextEmail = emailDraft.trim();
    if (!nextEmail || !resolvedActiveStudent) {
      return;
    }
    try {
      await dispatch(
        updateStudentContact({
          studentId: resolvedActiveStudent.id,
          email: nextEmail,
          phone: currentPhone,
        })
      ).unwrap();
      setEmailOverride(nextEmail);
      setIsEditingEmail(false);
      setEmailDraft('');
      await dispatch(getStudentDetail(resolvedActiveStudent.id));
    } catch (error) {
      console.error('Failed to update email', error);
    }
  };

  const handleEmailCancel = () => {
    setIsEditingEmail(false);
    setEmailDraft('');
  };

  const handlePhoneSave = async () => {
    const nextPhone = phoneDraft.trim();
    if (!nextPhone || !resolvedActiveStudent) {
      return;
    }
    try {
      await dispatch(
        updateStudentContact({
          studentId: resolvedActiveStudent.id,
          email: currentEmail,
          phone: nextPhone,
        })
      ).unwrap();
      setPhoneOverride(nextPhone);
      setIsEditingPhone(false);
      setPhoneDraft('');
      await dispatch(getStudentDetail(resolvedActiveStudent.id));
    } catch (error) {
      console.error('Failed to update phone', error);
    }
  };

  const handlePhoneCancel = () => {
    setIsEditingPhone(false);
    setPhoneDraft('');
  };

  const handlePostNote = async () => {
    if (!resolvedActiveStudent || isNoteEmpty) {
      return;
    }
    try {
      await dispatch(
        createNote({
          studentId: resolvedActiveStudent.id,
          content: noteDraft,
          date: Math.floor(Date.now() / 1000),
        })
      ).unwrap();
      setNoteDraft('');
      setShowNoteEditor(false);
      await dispatch(getStudentDetail(resolvedActiveStudent.id));
    } catch (error) {
      console.error('Failed to create note', error);
    }
  };

  const handleSharedResourceOpen = (resource: SharedResource) => {
    if (!resource.link) {
      return;
    }
    const fileUrl = resource.link;
    const extension = fileUrl.split('.').pop()?.split('?')[0]?.toLowerCase() ?? '';
    const isImage =
      resource.type === 'Image' ||
      ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension);
    const isDoc = ['doc', 'docx', 'odt', 'rtf'].includes(extension);
    const isPdf = resource.type === 'PDF' || extension === 'pdf';
    if (isImage) {
      setPreviewKind('image');
      setPreviewResource(resource);
      return;
    }
    if (isDoc) {
      setPreviewKind('doc');
      setPreviewResource(resource);
      return;
    }
    if (isPdf) {
      setPreviewKind('pdf');
      setPreviewResource(resource);
      return;
    }
    window.open(resource.link, '_blank', 'noopener,noreferrer');
  };



  const handleDragStart = (id: string, event: DragEvent<HTMLDivElement>) => {
    event.dataTransfer.setData('text/plain', id);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = async (
    status: StudentStatus,
    event: DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    const droppedId = event.dataTransfer.getData('text/plain');
    if (!droppedId || statusUpdating) {
      return;
    }
    const previousStatus =
      students.find((student) => student.id === droppedId)?.status ?? status;
    setStudents((prev) =>
      prev.map((student) =>
        student.id === droppedId ? { ...student, status } : student
      )
    );
    try {
      await dispatch(
        updateBookingStatus({
          studentId: droppedId,
          status,
        })
      ).unwrap();
    } catch (error) {
      setStudents((prev) =>
        prev.map((student) =>
          student.id === droppedId
            ? { ...student, status: previousStatus }
            : student
        )
      );
      console.error('Failed to update status', error);
    }
  };

  const studentsPanelData = {
    view,
    isDetailView,
    isLoading,
    hasError,
    students,
    sortedStudents,
    studentId,
    openStatusId,
    statusUpdating,
    updatingStatusId,
    sortKey,
    sortDirection,
    listGridCols,
    statusStyles,
    statusOptions,
    statusColumns,
    searchQuery,
  };
  const studentsPanelActions = {
    onViewChange: setView,
    onSort: handleSort,
    onSelectStudent: (id: string) => navigate(`/students/${id}`),
    onOpenStatusMenu: setOpenStatusId,
    onStatusSelect: handleStatusSelect,
    onOpenNotes: (id: string) =>
      navigate(`/students/${id}`, { state: { tab: 'Notes' } }),
    onOpenResources: (id: string) =>
      navigate(`/students/${id}`, { state: { tab: 'Shared Resources' } }),
    onOpenMessages: () => navigate('/messages'),
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
    onSearchChange: setSearchQuery,
  };
  const detailPanelData = {
    isLoading: detailLoading,
    hasDetailError,
    student: resolvedActiveStudent,
    detailTabs,
    activeTab,
    openStatusId,
    statusUpdating,
    updatingStatusId,
    statusStyles,
    statusOptions,
    isEditingEmail,
    emailDraft,
    displayEmail,
    isEditingPhone,
    phoneDraft,
    displayPhone,
    contactUpdating,
    showNoteEditor,
    noteDraft,
    noteModules,
    noteFormats,
    isPostDisabled,
    notePosting,
    resourceTypeStyles,
    formatResourceDate,
  };
  const detailPanelActions = {
    onBack: () => navigate('/students'),
    onToggleStatusMenu: setOpenStatusId,
    onStatusSelect: handleStatusSelect,
    onTabChange: (tab: DetailTab) => setActiveTab(tab),
    onEmailDraftChange: setEmailDraft,
    onEmailSave: handleEmailSave,
    onEmailCancel: handleEmailCancel,
    onEmailAdd: () => {
      setEmailDraft('');
      setIsEditingEmail(true);
    },
    onPhoneDraftChange: setPhoneDraft,
    onPhoneSave: handlePhoneSave,
    onPhoneCancel: handlePhoneCancel,
    onPhoneAdd: () => {
      setPhoneDraft('');
      setIsEditingPhone(true);
    },
    onNoteDraftChange: setNoteDraft,
    onToggleNoteEditor: setShowNoteEditor,
    onPostNote: handlePostNote,
    onOpenResource: handleSharedResourceOpen,
    onOpenMessages: (id: string) => navigate(`/messages?id=${id}`),
  };
  const previewModalData = {
    previewResource,
    previewKind,
  };
  const previewModalActions = {
    onClose: () => {
      setPreviewResource(null);
      setPreviewKind('none');
    },
  };

  return (
    <div
      className={`flex flex-col gap-6 ${isDetailView ? 'lg:flex-row' : ''}`}
    >
      <StudentsPanel data={studentsPanelData} actions={studentsPanelActions} />

      {isDetailView ? (
        <StudentDetailPanel
          data={detailPanelData}
          actions={detailPanelActions}
        />
      ) : null}

      <ResourcePreviewModal
        data={previewModalData}
        actions={previewModalActions}
      />
    </div>
  );
};

export default layout(Home);
